/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

==========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useEffect, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
} from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 React example components
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import MDButton from "components/MDButton";

const categories = [
  { label: "Email", value: "65d36a33dcbced2f3f12bfff" },
  { label: "Tools", value: "65d36a3bdcbced2f3f12c002" },
  { label: "Webinar", value: "65d36a45dcbced2f3f12c005" },
  { label: "Property", value: "65d36a4edcbced2f3f12c008" },
  { label: "Database", value: "65d36a57dcbced2f3f12c00b" },
  { label: "AI", value: "65d36a60dcbced2f3f12c00e" },
  { label: "Game", value: "65d36a68dcbced2f3f12c011" },
  { label: "Payement", value: "65d36a6fdcbced2f3f12c014" },
  { label: "E-Commerce", value: "65d36a7cdcbced2f3f12c017" },
  { label: "Sms", value: "65d36a84dcbced2f3f12c01a" },
  { label: "Crm", value: "65d36a8cdcbced2f3f12c01d" },
  { label: "Notification & Chat", value: "65d36a9adcbced2f3f12c020" },
  { label: "Task Managment", value: "65d36aa4dcbced2f3f12c023" },
  { label: "Analytics", value: "65d36ab9dcbced2f3f12c026" },
  { label: "Website Builder", value: "65d36adcdcbced2f3f12c029" },
  { label: "Onile Ordering", value: "65d36ae9dcbced2f3f12c02c" },
  { label: "Calendar", value: "65d36af4dcbced2f3f12c02f" },
  { label: "GPS & Location", value: "65d36affdcbced2f3f12c032" },
  { label: "Agency", value: "65d36b18dcbced2f3f12c035" },
  { label: "GHL Tools", value: "66e2d9a076355fb414c4c0db" },
  { label: "Project management", value: "66e2d9b876355fb414c4c0e5" },
  { label: "Widgets", value: "6724ea373971ec3f39cc6817" },
];

function DataTable({
  entriesPerPage,
  canSearch,
  typeOptions,
  canFilter,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  unlimitedFaq,
}) {
  const defaultValue = entriesPerPage ? entriesPerPage.defaultValue : 100;
  const entries = entriesPerPage?.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["10", "20", "30", "50", "100", "150", "200", "300", "400"];

  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize },
  } = tableInstance;

  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

  // Search and Filters state
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [publishFilter, setPublishFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [marketplaceFilter, setMarketplaceFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(null);

  // Debounced search
  const onSearchChange = useAsyncDebounce((value) => {
    applyFilters(value, typeFilter, publishFilter, statusFilter, marketplaceFilter, categoryFilter);
  }, 100);

  // Apply all filters together including category.name filter
  const applyFilters = (searchValue, type, publish, status, marketplace, category) => {
    let filterQuery = "";

    if (searchValue) filterQuery += searchValue + " ";
    if (type) filterQuery += type + " ";
    if (publish) filterQuery += publish + " ";
    if (status) filterQuery += status + " ";
    if (marketplace) filterQuery += marketplace + " ";
    if (category && category.label) filterQuery += category.label + " ";

    setGlobalFilter(filterQuery.trim() || undefined);
  };

  // Update global filter whenever filters change
  useEffect(() => {
    applyFilters(search, typeFilter, publishFilter, statusFilter, marketplaceFilter, categoryFilter);
  }, [search, typeFilter, publishFilter, statusFilter, marketplaceFilter, categoryFilter]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => setPageSize(value);

  // Render the paginations
  const renderPagination = pageOptions.map((option) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Setting the entries starting point
  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;
  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  return (
    <TableContainer sx={{ boxShadow: "none" }}>
      {(entriesPerPage || canSearch || canFilter) && (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          {/* Entries per page */}
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  setEntriesPerPage(parseInt(newValue, 10));
                }}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;entries per page
              </MDTypography>
            </MDBox>
          )}

          {/* Filters */}
          <MDBox display="flex" alignItems="center" gap={1}>
            {/* Type Filter */}
            {typeOptions && (
              <MDBox width="12rem">
                <Autocomplete
                  options={typeOptions || ["user", "agency", "location", "LevelUpMarketplace"]}
                  value={typeFilter}
                  onChange={(event, newValue) => setTypeFilter(newValue || "")}
                  renderInput={(params) => (
                    <MDInput {...params} placeholder="Select type..." size="small" fullWidth />
                  )}
                  disableClearable={false}
                  size="small"
                />
              </MDBox>
            )}

            {/* Draft/Publish Filter */}
            <MDBox width="12rem">
              <Autocomplete
                options={["Draft", "Publish"]}
                value={publishFilter}
                onChange={(event, newValue) => setPublishFilter(newValue || "")}
                renderInput={(params) => (
                  <MDInput {...params} placeholder="Draft/Publish..." size="small" fullWidth />
                )}
                disableClearable={false}
                size="small"
              />
            </MDBox>

            {/* Status Filter */}
            <MDBox width="12rem">
              <Autocomplete
                options={["active", "beta", "inactive"]}
                value={statusFilter}
                onChange={(event, newValue) => setStatusFilter(newValue || "")}
                renderInput={(params) => (
                  <MDInput {...params} placeholder="Select status..." size="small" fullWidth />
                )}
                disableClearable={false}
                size="small"
              />
            </MDBox>

            {/* Marketplace Status Filter */}
            <MDBox width="12rem">
              <Autocomplete
                options={["public", "private", "inprogress","roadmap","submitforreview"]}
                value={marketplaceFilter}
                onChange={(event, newValue) => setMarketplaceFilter(newValue || "")}
                renderInput={(params) => (
                  <MDInput {...params} placeholder="Marketplace Status..." size="small" fullWidth />
                )}
                disableClearable={false}
                size="small"
              />
            </MDBox>

            {/* Category Filter */}
            {/* <MDBox width="12rem">
              <Autocomplete
                options={categories}
                getOptionLabel={(option) => option.label || ""}
                value={categoryFilter}
                onChange={(event, newValue) => setCategoryFilter(newValue)}
                renderInput={(params) => (
                  <MDInput {...params} placeholder="Select category..." size="small" fullWidth />
                )}
                disableClearable={false}
                size="small"
              />
            </MDBox> */}
          </MDBox>

          {/* Search */}
          {canSearch && (
            <MDBox display="flex" alignItems="center" gap={1} width="18rem" ml="auto">
              {unlimitedFaq && (
                <MDButton color="primary" size="small" variant="contained" width="8rem">
                  Select Category
                </MDButton>
              )}
              <MDInput
                placeholder="Search..."
                value={search}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => {
                  setSearch(currentTarget.value);
                  onSearchChange(currentTarget.value);
                }}
              />
            </MDBox>
          )}
        </MDBox>
      )}

      {/* Table */}
      <Table {...getTableProps()}>
        <MDBox component="thead">
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
              {unlimitedFaq && (
                <DataTableHeadCell width={"4px"} align={"left"}>
                  Change Category
                </DataTableHeadCell>
              )}

              {headerGroup.headers?.map((column) => (
                <DataTableHeadCell
                  key={column.id}
                  {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                  width={column.width ? column.width : "auto"}
                  align={column.align ? column.align : "left"}
                  sorted={setSortedValue(column)}
                >
                  {column.render("Header")}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {page.map((row, key) => {
            prepareRow(row);
            return (
              <TableRow {...row.getRowProps()} key={row.id}>
                {unlimitedFaq && (
                  <DataTableBodyCell align={"left"}>
                    <input type="checkbox" height={10} width={10} />
                  </DataTableBodyCell>
                )}

                {row.cells?.map((cell) => (
                  <DataTableBodyCell
                    key={cell.column.id}
                    noBorder={noEndBorder && rows.length - 1 === key}
                    align={cell.column.align ? cell.column.align : "left"}
                    {...cell.getCellProps()}
                  >
                    {cell.render("Cell")}
                  </DataTableBodyCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Pagination */}
      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {rows.length} entries
            </MDTypography>
          </MDBox>
        )}
        {pageOptions.length > 1 && (
          <MDPagination
            variant={pagination.variant ? pagination.variant : "gradient"}
            color={pagination.color ? pagination.color : "info"}
          >
            {canPreviousPage && (
              <MDPagination item onClick={() => previousPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </MDPagination>
            )}
            {renderPagination.length > 6 ? (
              <MDBox width="5rem" mx={1}>
                <MDInput
                  inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
                  value={customizedPageOptions[pageIndex]}
                  onChange={({ target: { value } }) => gotoPage(Number(value) - 1)}
                />
              </MDBox>
            ) : (
              renderPagination
            )}
            {canNextPage && (
              <MDPagination item onClick={() => nextPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox>
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  canFilter: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  canFilter: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
};

export default DataTable;
